import React, { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.heat";
import { apiService } from "../../services/api";
import type {
  ProcessedRoute,
  HeatmapData,
  HeatmapOptions,
} from "../../types/route";

interface RouteHeatmapLayerProps {
  routes: ProcessedRoute[];
  options?: Partial<HeatmapOptions>;
  enabled: boolean;
  onDataUpdate?: (data: HeatmapData) => void;
}

export const RouteHeatmapLayer: React.FC<RouteHeatmapLayerProps> = ({
  routes,
  options = {},
  enabled,
  onDataUpdate,
}) => {
  const map = useMap();
  const heatLayerRef = useRef<L.HeatLayer | null>(null);

  // Default heatmap options
  const defaultOptions: HeatmapOptions = {
    radius: 20,
    maxZoom: 17,
    minOpacity: 0.3,
    maxOpacity: 0.8,
    blur: 15,
    gradient: {
      0.0: "#313695",
      0.1: "#4575b4",
      0.2: "#74add1",
      0.3: "#abd9e9",
      0.4: "#e0f3f8",
      0.5: "#ffffbf",
      0.6: "#fee090",
      0.7: "#fdae61",
      0.8: "#f46d43",
      0.9: "#d73027",
      1.0: "#a50026",
    },
  };

  const heatmapOptions = { ...defaultOptions, ...options };

  // Process routes into heatmap data using the API service
  const processRoutesToHeatmapData = (
    routes: ProcessedRoute[]
  ): HeatmapData => {
    // Use the API service for client-side processing
    return apiService.generateClientSideHeatmap(routes, 0.001);
  };

  useEffect(() => {
    if (!enabled || routes.length === 0) {
      // Remove existing heatmap layer
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }
      return;
    }

    // Process routes into heatmap data using the API service
    const rawHeatmapData = processRoutesToHeatmapData(routes);

    // Optimize the data for better performance
    const heatmapData = apiService.optimizeHeatmapData(rawHeatmapData, 5000, 2);

    // Notify parent component of data update
    if (onDataUpdate) {
      onDataUpdate(heatmapData);
    }

    if (heatmapData.points.length === 0) {
      return;
    }

    // Convert to Leaflet heat layer format
    const heatPoints: L.HeatLatLngTuple[] = heatmapData.points.map((point) => [
      point.latitude,
      point.longitude,
      point.intensity / heatmapData.maxIntensity, // Normalize intensity (0-1)
    ]);

    // Remove existing layer
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
    }

    // Create new heatmap layer
    heatLayerRef.current = L.heatLayer(heatPoints, {
      radius: heatmapOptions.radius,
      blur: heatmapOptions.blur,
      maxZoom: heatmapOptions.maxZoom,
      max: 1.0,
      minOpacity: heatmapOptions.minOpacity,
      gradient: heatmapOptions.gradient,
    });

    // Add to map
    heatLayerRef.current.addTo(map);

    // Cleanup function
    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }
    };
  }, [map, routes, enabled, heatmapOptions, onDataUpdate]);

  // Component cleanup
  useEffect(() => {
    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }
    };
  }, [map]);

  return null; // This component doesn't render anything visible
};
